import { Warning } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../store/actions';
import { i18n } from '../../translate/i18n';

interface IProps {
  version: 'app-bar' | 'side-bar' | 'table' | 'form';
}

const styles = {
  textBold: {
    fontWeight: 700,
    color: '#00000099'
  }
}

export const EquipmentsRegistrationLimitWarning = ({ version = 'table' }: IProps) => {
  const dispatch = useDispatch();

  const tenant = useSelector((state: any) => state.auth.selectedAccess.tenant);
  const registeredEquipments = useSelector((state: any) => state.tenants.registeredEquipments);

  const equipmentsRegistrationLimit = tenant?.equipmentsRegistrationLimit;

  const toggleEquipmentsLimitModal = useCallback(() => dispatch(actions.toggleEquipmentsLimitModal()), [dispatch])

  let content = <></>

  if (!equipmentsRegistrationLimit || equipmentsRegistrationLimit.maxEquipments === 0 || registeredEquipments === null) {
    return content;
  }

  const {
    maxEquipmentsTolerated = 0,
    maxEquipments = 0,
    tolerance = 0
  } = equipmentsRegistrationLimit;

  let reachedPercentage = (maxEquipments > 0) ? parseFloat((registeredEquipments/maxEquipments * 100).toFixed(2)) : 100;
  reachedPercentage = reachedPercentage > 100 ? 100 : reachedPercentage;

  let reachedPercentageOverLimit = (registeredEquipments > maxEquipments) ? parseFloat(((registeredEquipments - maxEquipments)/(maxEquipmentsTolerated - maxEquipments) * 100).toFixed(2)) : 0;
  reachedPercentageOverLimit = reachedPercentageOverLimit > 100 ? 100 : reachedPercentageOverLimit

  if (reachedPercentage < 95) {
    return content;
  }
  
  const BarWithinLimit = (
    <Box sx={{background: '#eee', position: 'relative', borderRadius: '2px 0px 0px 2px', width: `${100 - tolerance}%`, height: '10px'}}>
      <Box
        sx={{
          background: '#4CAF50',
          position: 'absolute',
          left: 0,
          borderRadius: '2px 0px 0px 2px',
          width: `${reachedPercentage}%`,
          height: '100%'
        }}
      />
    </Box>
  );

  const BarOverLimit = (
    <Box sx={{background: '#eee', position: 'relative', borderRadius: '0px 2px 2px 0px', width: `${tolerance}%`, height: '10px', marginLeft: '2px'}}>
      <Box
        sx={{
          background: '#d32f2f',
          position: 'absolute',
          left: 0,
          borderRadius: '0px 2px 2px 0px',
          width: `${reachedPercentageOverLimit}%`,
          height: '100%'
        }}
      />
    </Box>
  )

  if (version === 'app-bar') {
    content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          background: '#FBEEEE',
          padding: '4px 12px',
          textAlign: 'center',
          borderRadius: '15px',
          color: '#D32F2F',
        }}
        onClick={() => toggleEquipmentsLimitModal()}
      >
        <Warning color="error" />

        <Typography>
          {`${i18n.t('equipmentsRegistrationLimitWarning.limitPercentage.appBar', { percentage: reachedPercentage })}`}
        </Typography>
      </Box>
    );

  } else if (version === 'side-bar') {
    content = (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        background: '#d32f2f14',
        padding: '12px 16px',
        textAlign: 'center'
      }}>
        <Warning color="error" />

        <Typography sx={styles.textBold}>
          {`${i18n.t('equipmentsRegistrationLimitWarning.limitPercentage.sideBar', { percentage: reachedPercentage })}`}
        </Typography>

        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', marginBottom: '20px'}}>
          <Typography>
            <span style={styles.textBold}>{registeredEquipments}</span>/{maxEquipments} {i18n.t('equipmentsRegistrationLimitWarning.registeredEquipments')}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
            {BarWithinLimit}
            {BarOverLimit}
          </Box>
        </Box>

        <Button
          variant="outlined"
          size="small"
          color="error"
          sx={{fontSize: '12px'}}
          onClick={() => toggleEquipmentsLimitModal()}
        >
          {`${i18n.t('equipmentsRegistrationLimitWarning.contactAccountables')}`}
        </Button>
      </Box>
    );

  } else if (version === 'table') {
    content = (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 5,
        background: '#d32f2f14',
        padding: '12px 16px',
        marginBottom: '20px',
        borderRadius: '8px'
      }}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
              <Warning color="error" />
              <Typography sx={styles.textBold}>
                {`${i18n.t('equipmentsRegistrationLimitWarning.limitPercentage.table', { percentage: reachedPercentage })}`}
              </Typography>
            </Box>
            <Typography>{`${i18n.t('equipmentsRegistrationLimitWarning.reachedInfoPrefix')}`} <span style={styles.textBold}>{reachedPercentage}% {`${i18n.t('equipmentsRegistrationLimitWarning.reachedInfoSuffix')}`}</span>, {`${i18n.t('equipmentsRegistrationLimitWarning.reachedInfoText')}`}.</Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={{fontSize: '12px'}}
            onClick={() => toggleEquipmentsLimitModal()}
          >
            {`${i18n.t('equipmentsRegistrationLimitWarning.contactAccountables')}`}
          </Button>
        </Box>

        <Box sx={{display: 'flex', flexDirection: 'column'}}>
          <Typography>
            <span style={styles.textBold}>{registeredEquipments}</span>/{maxEquipments} {`${i18n.t('equipmentsRegistrationLimitWarning.registeredEquipments')}`}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
            {BarWithinLimit}
            {BarOverLimit}
          </Box>
        </Box>
      </Box>
    );

  } else if (version === 'form') {
    content = (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        background: '#d32f2f14',
        padding: '12px 16px',
        marginBottom: '20px',
        borderRadius: '10px'
      }}>
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center',  justifyContent: 'center', gap: 1, width: '50%'}}>
            <Warning color="error" />
            <Typography sx={styles.textBold}>
              {`${i18n.t('equipmentsRegistrationLimitWarning.limitPercentage.table', { percentage: reachedPercentage })}`}
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
              {BarWithinLimit}
              {BarOverLimit}
            </Box>
          </Box>
          <Typography>{`${i18n.t('equipmentsRegistrationLimitWarning.reachedInfoPrefix')}`} <span style={styles.textBold}>{reachedPercentage}% {`${i18n.t('equipmentsRegistrationLimitWarning.reachedInfoSuffix')}`}</span>, {`${i18n.t('equipmentsRegistrationLimitWarning.reachedInfoText')}`}.</Typography>
          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={{fontSize: '12px'}}
            onClick={() => toggleEquipmentsLimitModal()}
          >
            {`${i18n.t('equipmentsRegistrationLimitWarning.contactAccountables')}`}
          </Button>
      </Box>
    );
  }

  
  return (
    <>
      {content}
    </>
  );
}